import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { SocialService } from '../../services/socialService';
import AuthShell, { AuthDivider } from '../../components/AuthShell/AuthShell';
import SocialAuthButtons from '../../components/AuthShell/SocialAuthButtons';
import styles from '../../components/AuthShell/authForm.module.css';

const USERNAME_REGEX = /^[a-z0-9_]{3,20}$/;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const USERNAME_MIN_LENGTH = 3;

function validate({ displayName, username, email, password, dateOfBirth }) {
    const e = {};
    if (!displayName.trim() || displayName.trim().length < 2)
        e.displayName = 'Display name must be at least 2 characters.';
    if (!USERNAME_REGEX.test(username))
        e.username = '3–20 characters: lowercase letters, numbers, underscores only.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
        e.email = 'Enter a valid email address.';
    if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/\d/.test(password))
        e.password = 'Min 8 characters with a letter and a number.';

    // DOB validation
    if (!dateOfBirth) {
        e.dateOfBirth = 'Date of birth is required.';
    } else {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        if (age < 13) {
            e.dateOfBirth = 'You must be at least 13 years old to use Aluvo.';
        }
    }

    return { valid: Object.keys(e).length === 0, errors: e };
}

function suggestUsername(name) {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 20);
}

function Field({ id, label, error, hint, hintTone, children }) {
    return (
        <div className={styles.field}>
            <label className={styles.label} htmlFor={id}>{label}</label>
            {children}
            {error && <span id={`${id}-error`} className={styles.fieldError} role="alert">{error}</span>}
            {!error && hint && (
                <span id={`${id}-hint`} className={`${styles.fieldHint} ${hintTone ? styles[hintTone] : ''}`}>
                    {hint}
                </span>
            )}
        </div>
    );
}

export default function Register() {
    const { register, loginWithGoogle, loginWithApple } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fromState = location.state?.from;
    const from = fromState?.pathname
        ? `${fromState.pathname || '/'}${fromState.search || ''}${fromState.hash || ''}`
        : null;
    const emailMethodTracked = useRef(false);

    // Social signup reuses the exact same AuthContext actions as login; the
    // backend upserts the account for a first-time Google/Apple identity.
    const handleGoogleCredential = async (credential) => {
        setErrors({});
        const outcome = await loginWithGoogle(credential);
        if (!outcome?.deleted && !outcome?.destination) navigate(from || '/', { replace: true });
    };

    const handleAppleIdentity = async (identity) => {
        setErrors({});
        const outcome = await loginWithApple(identity);
        if (!outcome?.deleted && !outcome?.destination) navigate(from || '/', { replace: true });
    };

    const [displayName, setDisplayName] = useState('');
    const [username, setUsername] = useState('');
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const turnstileContainerRef = useRef(null);
    const turnstileWidgetRef = useRef(null);
    const usernameCheckSeq = useRef(0);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [usernameAvailability, setUsernameAvailability] = useState({
        status: 'idle',
        username: '',
    });

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY) return undefined;

        let cancelled = false;

        const renderTurnstile = () => {
            if (
                cancelled ||
                !window.turnstile ||
                !turnstileContainerRef.current ||
                turnstileWidgetRef.current
            ) {
                return;
            }

            turnstileWidgetRef.current = window.turnstile.render(turnstileContainerRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                theme: 'auto',
                callback: (token) => {
                    setTurnstileToken(token);
                    setErrors((p) => ({ ...p, form: undefined }));
                },
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => {
                    setTurnstileToken('');
                    setErrors((p) => ({
                        ...p,
                        form: "We couldn't verify this signup. Please try again.",
                    }));
                },
            });
        };

        if (window.turnstile) {
            renderTurnstile();
        } else {
            const existing = document.querySelector('script[data-turnstile-script="true"]');
            if (!existing) {
                const script = document.createElement('script');
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                script.async = true;
                script.defer = true;
                script.dataset.turnstileScript = 'true';
                script.onload = renderTurnstile;
                document.head.appendChild(script);
            } else {
                existing.addEventListener('load', renderTurnstile, { once: true });
            }
        }

        return () => {
            cancelled = true;
            if (window.turnstile && turnstileWidgetRef.current) {
                window.turnstile.remove(turnstileWidgetRef.current);
            }
            turnstileWidgetRef.current = null;
        };
    }, []);

    const handleDisplayName = (v) => {
        setDisplayName(v);
        if (!usernameTouched) setUsername(suggestUsername(v));
        if (errors.displayName) setErrors(p => ({ ...p, displayName: undefined }));
    };

    const handleUsername = (v) => {
        setUsernameTouched(true);
        setUsername(v.toLowerCase().replace(/[^a-z0-9_]/g, ''));
        if (errors.username) setErrors(p => ({ ...p, username: undefined }));
    };

    useEffect(() => {
        const value = username.trim().toLowerCase();
        usernameCheckSeq.current += 1;
        const seq = usernameCheckSeq.current;

        if (!value) {
            setUsernameAvailability({ status: 'idle', username: value });
            return undefined;
        }

        if (value.length < USERNAME_MIN_LENGTH || !USERNAME_REGEX.test(value)) {
            setUsernameAvailability({ status: 'invalid', username: value });
            return undefined;
        }

        setUsernameAvailability({ status: 'checking', username: value });

        const timer = setTimeout(async () => {
            try {
                const result = await SocialService.checkUsernameAvailability(value);
                if (seq !== usernameCheckSeq.current) return;

                setUsernameAvailability({
                    status: result.available ? 'available' : 'taken',
                    username: value,
                });
            } catch {
                if (seq !== usernameCheckSeq.current) return;
                setUsernameAvailability({ status: 'error', username: value });
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [username]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!emailMethodTracked.current) {
            emailMethodTracked.current = true;
            trackEvent(ANALYTICS_EVENTS.AUTH_METHOD_SELECTED, { platform: 'web', mode: 'signup', method: 'email' });
        }
        const { valid, errors: ve } = validate({ displayName, username, email, password, dateOfBirth });
        if (!valid) { setErrors(ve); return; }
        if (usernameAvailability.username === username.trim().toLowerCase()) {
            if (usernameAvailability.status === 'taken') {
                setErrors({ username: 'This username is already taken.' });
                return;
            }
            if (usernameAvailability.status === 'checking') {
                setErrors({ username: 'Still checking username availability.' });
                return;
            }
        }
        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            setErrors({ form: "We couldn't verify this signup. Please try again." });
            return;
        }
        trackEvent(ANALYTICS_EVENTS.SIGNUP_STARTED, { source: 'direct' }, { onceKey: 'register-form' });
        setErrors({});
        setLoading(true);
        try {
            const outcome = await register({
                email: email.trim().toLowerCase(),
                password,
                displayName: displayName.trim(),
                username: username.trim(),
                dateOfBirth,
                turnstileToken: turnstileToken || undefined,
            });
            // AuthContext routes new accounts into onboarding (or DOB) itself; a
            // gated guest's `from` is resumed after onboarding via the pending intent.
            if (!outcome?.deleted && !outcome?.destination && from) navigate(from, { replace: true });
        } catch (err) {
            const msg = err.message || '';
            if (err.statusCode === 409 || /email already in use/i.test(msg))
                setErrors({ email: 'An account with this email already exists.' });
            else if (/username.*taken|username.*use/i.test(msg))
                setErrors({ username: 'This username is already taken.' });
            else if (/age|13|dob|birth/i.test(msg))
                setErrors({ dateOfBirth: msg });
            else
                setErrors({ form: msg || 'Registration failed. Please try again.' });
            if (window.turnstile && turnstileWidgetRef.current) {
                window.turnstile.reset(turnstileWidgetRef.current);
                setTurnstileToken('');
            }
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <>
            <p className={styles.legalText}>
                By creating an account, you agree to our{' '}
                <Link to="/terms" className={styles.legalLink}>Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className={styles.legalLink}>Privacy Policy</Link>.
            </p>
            <p className={styles.switchText}>
                Already have an account?{' '}
                <Link to="/login" state={location.state} className={styles.link}>Sign in</Link>
            </p>
        </>
    );

    return (
        <AuthShell
            mode="signup"
            title="Start your collection"
            subtitle="Verify what you've watched, earn coins, and build a collection that proves your fandom."
            pageTitle="Create your account · Aluvo"
            footer={footer}
        >
            <SocialAuthButtons
                mode="signup"
                onGoogleCredential={handleGoogleCredential}
                onAppleIdentity={handleAppleIdentity}
                onError={(message) => setErrors({ form: message })}
                disabled={loading}
            />

            <AuthDivider label="or continue with email" />

            <form className={`${styles.form} ${styles.formRoomy}`} onSubmit={handleSubmit} noValidate data-clarity-mask="true" aria-label="Create account with email">
                <Field id="register-display-name" label="Display Name" error={errors.displayName}>
                    <input
                        id="register-display-name"
                        className={`${styles.input} ${errors.displayName ? styles.inputError : ''}`}
                        type="text"
                        placeholder="e.g. Alex Rivera"
                        value={displayName}
                        onChange={(e) => handleDisplayName(e.target.value)}
                        autoCapitalize="words"
                        autoComplete="name"
                        aria-invalid={errors.displayName ? 'true' : undefined}
                        aria-describedby={errors.displayName ? 'register-display-name-error' : undefined}
                    />
                </Field>

                <Field
                    id="register-username"
                    label="Username"
                    error={errors.username}
                    hintTone={
                        usernameAvailability.status === 'available'
                            ? 'hintSuccess'
                            : usernameAvailability.status === 'taken' || usernameAvailability.status === 'error'
                                ? 'hintError'
                                : undefined
                    }
                    hint={
                        usernameAvailability.status === 'available'
                            ? 'Available'
                            : usernameAvailability.status === 'checking'
                                ? 'Checking availability...'
                                : usernameAvailability.status === 'taken'
                                    ? 'This username is already taken.'
                                    : usernameAvailability.status === 'error'
                                        ? 'Could not check availability right now.'
                                        : 'Used in your public profile link.'
                    }
                >
                    <div className={`${styles.usernameWrap} ${errors.username || usernameAvailability.status === 'taken' ? styles.inputError : ''} ${usernameAvailability.status === 'available' ? styles.inputSuccess : ''}`}>
                        <span className={styles.atSign} aria-hidden="true">@</span>
                        <input
                            id="register-username"
                            className={styles.usernameInput}
                            type="text"
                            placeholder="your_username"
                            value={username}
                            onChange={(e) => handleUsername(e.target.value)}
                            autoCapitalize="none"
                            autoCorrect="off"
                            maxLength={20}
                            aria-invalid={errors.username ? 'true' : undefined}
                            aria-describedby={errors.username ? 'register-username-error' : 'register-username-hint'}
                        />
                        {usernameAvailability.status === 'available' && (
                            <span className={styles.usernameStatus} aria-label="Username available">✓</span>
                        )}
                        {usernameAvailability.status === 'checking' && (
                            <span className={styles.usernameChecking} aria-label="Checking username availability" />
                        )}
                        <span className={`${styles.charCount} ${username.length >= 18 ? styles.charWarn : ''}`} aria-hidden="true">
                            {username.length}/20
                        </span>
                    </div>
                </Field>

                <Field id="register-email" label="Email" error={errors.email}>
                    <input
                        id="register-email"
                        className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
                        autoComplete="email"
                        autoCapitalize="none"
                        aria-invalid={errors.email ? 'true' : undefined}
                        aria-describedby={errors.email ? 'register-email-error' : undefined}
                    />
                </Field>

                <Field
                    id="register-dob"
                    label="Date of Birth"
                    error={errors.dateOfBirth}
                    hint="You must be at least 13 years old."
                >
                    <input
                        id="register-dob"
                        className={`${styles.input} ${errors.dateOfBirth ? styles.inputError : ''}`}
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => {
                            setDateOfBirth(e.target.value);
                            setErrors(p => ({ ...p, dateOfBirth: undefined }));
                        }}
                        max={new Date().toISOString().split('T')[0]}
                        min="1900-01-01"
                        required
                        aria-invalid={errors.dateOfBirth ? 'true' : undefined}
                        aria-describedby={errors.dateOfBirth ? 'register-dob-error' : 'register-dob-hint'}
                    />
                </Field>

                <Field
                    id="register-password"
                    label="Password"
                    error={errors.password}
                    hint="Min 8 characters with a letter and number."
                >
                    <div className={`${styles.passwordWrap} ${errors.password ? styles.inputError : ''}`}>
                        <input
                            id="register-password"
                            className={styles.passwordInput}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create a password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                            autoComplete="new-password"
                            aria-invalid={errors.password ? 'true' : undefined}
                            aria-describedby={errors.password ? 'register-password-error' : 'register-password-hint'}
                        />
                        <button
                            type="button"
                            className={styles.eyeBtn}
                            onClick={() => setShowPassword(s => !s)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            aria-pressed={showPassword}
                        >
                            {showPassword ? '🙈' : '👁️'}
                        </button>
                    </div>
                </Field>

                {TURNSTILE_SITE_KEY && (
                    <div className={styles.turnstileWrap} ref={turnstileContainerRef} />
                )}

                {errors.form && <p className={styles.errorMsg} role="alert">{errors.form}</p>}

                <button className={styles.btnPrimary} type="submit" disabled={loading} aria-busy={loading || undefined}>
                    {loading ? <span className={styles.spinner} aria-label="Creating account" /> : 'Create Account'}
                </button>
            </form>
        </AuthShell>
    );
}
